import {
    getCountries,
    getCountryCallingCode,
    isValidPhoneNumber,
    parsePhoneNumberFromString,
} from "libphonenumber-js/max";
import { Metadata } from "libphonenumber-js/core";
import maxMetadata from "libphonenumber-js/metadata.max.json";

// Countries listed first in the dial-code picker.
const PREFERRED = ["PL", "DE", "CZ", "SK", "UA", "LT", "GB", "IE", "NL", "FR", "IT", "ES", "AT", "NO", "SE", "US"];

const metadata = new Metadata(maxMetadata);

/** Longest national number (mobile or landline) for a country, e.g. PL -> 9. */
export const maxNationalLength = (country) => {
    try {
        metadata.selectNumberingPlan(country);
        const plan = metadata.numberingPlan;
        const lengths = ["MOBILE", "FIXED_LINE"]
            .map((type) => plan.type(type)?.possibleLengths() || [])
            .flat();
        const all = lengths.length ? lengths : plan.possibleLengths();
        return Math.max(...all);
    } catch {
        return 15;
    }
};

const flagOf = (country) =>
    String.fromCodePoint(...[...country].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

/** Dial-code options with names in the shop's language. */
export const getPhoneCountries = (locale) => {
    let names;
    try {
        names = new Intl.DisplayNames([locale === "sp" ? "es" : locale], { type: "region" });
    } catch {
        names = null;
    }
    const all = getCountries().map((code) => ({
        code,
        dial: getCountryCallingCode(code),
        name: names?.of(code) || code,
        flag: flagOf(code),
    }));
    const preferred = PREFERRED.map((c) => all.find((o) => o.code === c)).filter(Boolean);
    const rest = all
        .filter((o) => !PREFERRED.includes(o.code))
        .sort((a, b) => a.name.localeCompare(b.name, locale));
    return [...preferred, ...rest];
};

/** "+48512345678" -> { country: "PL", national: "512345678" }. */
export const splitPhone = (e164, fallbackCountry = "PL") => {
    const parsed = parsePhoneNumberFromString(String(e164 || ""));
    if (parsed?.country) return { country: parsed.country, national: parsed.nationalNumber };
    const dial = getCountryCallingCode(fallbackCountry);
    const digits = String(e164 || "").replace(/\D/g, "");
    return {
        country: fallbackCountry,
        national: digits.startsWith(dial) ? digits.slice(dial.length) : digits,
    };
};

export const joinPhone = (country, national) => `+${getCountryCallingCode(country)}${national}`;

export const isValidPhone = (e164) => typeof e164 === "string" && /^\+\d+$/.test(e164) && isValidPhoneNumber(e164);
